// ADR-0160 — lo que el panel de gestión dice de una visita que propuso el
// contratista desde su enlace: quién y cuándo la agendó, que sigue por
// confirmar con el propietario, si cae después del plazo y las fechas
// anteriores del ciclo. Puro, para fijarlo con un test sin montar el panel.
// Las etiquetas viven en api-types (fuente única con la PWA).

import {
  fechaHoraSantiagoLabel,
  preferenciaHorarioLabel,
  propuestaPorContratistaLabel,
  VISITA_POR_CONFIRMAR,
  visitaConfirmacionLabel,
  visitaLabel,
  visitaDiaCortoLabel,
  type FindingDetailResponse,
} from "@bitacorapro/api-types";
import { OBSERVATION_VISIT_CONTRACTOR_MAX_CHANGES } from "@bitacorapro/core-engine";

type Proposal = NonNullable<FindingDetailResponse["visitProposal"]>;
type History = NonNullable<FindingDetailResponse["visitHistory"]>;
type Rango = { date: string; startTime: string; endTime: string };

export type VisitaPropuestaRows = {
  /** «El contratista, el 17-oct a las 09:30 · Por confirmar con el
   *  propietario» (va bajo la clave «Agendó»). null si la agendó el
   *  personal. */
  agendo: string | null;
  /** Advertencia (no bloquea): la visita cae después del plazo. */
  despuesDelPlazo: string | null;
  /** «jueves 16-oct, 9:00 a 13:00 (contratista)», más reciente primero,
   *  sin la vigente. Si la visita se borró (pedido de otra visita,
   *  reasignar sin fecha), todas: la primera ya no es la vigente. */
  antes: string[];
  /** ADR-0161 — el estado de la confirmación para la fila «Confirmación»
   *  («Por confirmar · se confirma sola el … si el propietario no
   *  responde», «Confirmada por el propietario el …»). null sin propuesta
   *  o contra un core-api anterior. */
  confirmacion: string | null;
  /** ADR-0161 — el propietario pidió otro horario: lo que pidió y si el
   *  contratista todavía puede proponer otra desde su enlace. */
  otroHorario: { texto: string; contratistaPuede: boolean } | null;
  /** ADR-0161 — por qué esta visita no se confirmará sola. */
  avisoNoEnviado: string | null;
  /** El aviso del formulario «Cambiar visita». */
  hint: string | null;
};

export function visitaPropuestaRows(
  proposal: Proposal | null | undefined,
  history: History | null | undefined,
  /** La visita vigente (`observation.visit`): el historial no la marca. */
  visit: Rango | null | undefined,
  ahora: Date = new Date()
): VisitaPropuestaRows {
  const pendiente = proposal?.confirmation === "pending";
  const filas = history ?? [];
  // La primera fila es la vigente sólo si sigue en pie (mismo día y rango).
  const primeraEsVigente =
    visit != null &&
    filas[0] !== undefined &&
    filas[0].date === visit.date &&
    filas[0].startTime === visit.startTime &&
    filas[0].endTime === visit.endTime;
  return {
    agendo: proposal
      ? `${propuestaPorContratistaLabel(proposal.proposedAt)}${pendiente ? ` · ${VISITA_POR_CONFIRMAR}` : ""}`
      : null,
    despuesDelPlazo: proposal?.afterDue ? "Queda después del plazo de la observación." : null,
    antes: (primeraEsVigente ? filas.slice(1) : filas).map(
      (h) => `${visitaLabel(h)} (${h.proposedBy === "contractor" ? "contratista" : "inmobiliaria"})`
    ),
    ...respuestaRows(proposal, ahora),
  };
}

const DIA_SANTIAGO = new Intl.DateTimeFormat("en-CA", {
  timeZone: "America/Santiago",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

/** ADR-0161 — lo que el panel dice de la respuesta del propietario. */
function respuestaRows(
  proposal: Proposal | null | undefined,
  ahora: Date
): Pick<VisitaPropuestaRows, "confirmacion" | "otroHorario" | "avisoNoEnviado" | "hint"> {
  if (!proposal) return { confirmacion: null, otroHorario: null, avisoNoEnviado: null, hint: null };
  const c = proposal.confirmation;
  const respondida = proposal.respondedAt
    ? visitaDiaCortoLabel(DIA_SANTIAGO.format(new Date(proposal.respondedAt)))
    : null;
  let confirmacion = visitaConfirmacionLabel(c);
  if (c === "pending" && proposal.confirmBy && new Date(proposal.confirmBy) > ahora) {
    confirmacion = `${confirmacion} · se confirma sola el ${fechaHoraSantiagoLabel(proposal.confirmBy)} si el propietario no responde`;
  } else if ((c === "confirmed" || c === "auto_confirmed") && respondida) {
    confirmacion = `${confirmacion} el ${respondida}`;
  }
  const contratistaPuede =
    proposal.contractorLinkActive !== false &&
    proposal.changesUsed < OBSERVATION_VISIT_CONTRACTOR_MAX_CHANGES;
  const otroHorario =
    c === "other_time_requested" && proposal.ownerPreference
      ? {
          texto: preferenciaHorarioLabel(
            proposal.ownerPreference.dates,
            proposal.ownerPreference.slot
          ),
          contratistaPuede,
        }
      : null;
  let avisoNoEnviado: string | null = null;
  if (c === "pending") {
    const b = proposal.autoConfirmBlocked;
    // El cambio de dueño primero: con él el correo tampoco sale
    // (`no_recipient`), pero el «coordínala con él» apuntaría al anterior.
    if (b === "owner_changed") {
      avisoNoEnviado = "La unidad cambió de propietario: coordina la visita.";
    } else if (proposal.ownerNotice === "not_tracked") {
      avisoNoEnviado = AVISO_SIN_CONSTANCIA;
    } else if (
      proposal.ownerNotice === "failed" ||
      proposal.ownerNotice === "no_recipient" ||
      b === "notice_failed" ||
      b === "no_notice"
    ) {
      avisoNoEnviado = AVISO_NO_ENVIADO;
    } else if (b === "link_inactive") {
      avisoNoEnviado = "El enlace del contratista ya no está activo.";
    } else if (b === "no_deadline") {
      avisoNoEnviado =
        "El aviso al propietario salió sin hora límite: esta visita no se confirmará sola.";
    } else if (b === "job_off") {
      avisoNoEnviado = AVISO_SIN_CONFIRMACION_AUTOMATICA;
    }
  }
  const hint =
    c === "other_time_requested"
      ? "El propietario pidió otro horario: elige uno distinto al que rechazó."
      : c === "confirmed" || c === "auto_confirmed"
        ? "Ya está confirmada; si la cambias, el propietario verá el nuevo horario en la app."
        : HINT_REEMPLAZA_PROPUESTA;
  return { confirmacion, otroHorario, avisoNoEnviado, hint };
}

/** El correo «Visita por confirmar» no salió (o no hay a quién): la
 *  visita no se confirma sola y la coordina el jefe. «Mientras»: el job
 *  puede reintentarlo, y si sale, la visita vuelve a tener hora límite. */
export const AVISO_NO_ENVIADO =
  "No pudimos avisar al propietario por correo. Mientras no le llegue, esta visita no se confirmará sola: coordínala con él.";

/** Una propuesta anterior a ADR-0161: el propietario sí recibió el aviso
 *  de entonces, pero sin constancia nunca se confirma sola. */
export const AVISO_SIN_CONSTANCIA =
  "Esta visita se propuso antes de la confirmación automática: no se confirmará sola. Si el propietario no responde, coordínala con él.";

/** La confirmación automática no corre (job apagado o sin activación). */
export const AVISO_SIN_CONFIRMACION_AUTOMATICA =
  "La confirmación automática no está activa: si el propietario no responde, coordina la visita con él.";

/** El aviso del formulario «Cambiar visita» cuando la vigente la propuso
 *  el contratista. */
export const HINT_REEMPLAZA_PROPUESTA =
  "La propuso el contratista; si la cambias, reemplazas su propuesta.";
